import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, MapPin, Check } from "lucide-react";
import { SERVICES, SEGMENTS, CAPABILITIES, PROBLEMS, SPEND_CLASSES, ENGAGEMENT_MODELS, CITIES, waLink } from "../lib/site";
import { Reveal, MaskLine, FadeIn } from "../components/motion";
import Mandala from "../components/Mandala";
import IndiaMap from "../components/IndiaMap";
import Crossover from "../components/Crossover";
import CTABanner from "../components/CTABanner";
import useMeta from "../lib/useMeta";

const CHAPTERS = [
  { num: "01", title: "Supply", text: "Everyday workplace essentials — housekeeping, pantry, stationery, electricals — delivered on schedule, without the chase." },
  { num: "02", title: "Source", text: "If it exists, we will find it at the right price. If it does not, we will get it made. Specialised sourcing is our craft." },
  { num: "03", title: "Run", text: "Audits, replenishment and operations support that keep your workplace stocked, compliant and humming." },
];

const STEPS = [
  { num: "01", title: "Share your requirement", text: "One message, one form or one call — that is all it takes." },
  { num: "02", title: "We source & quote", text: "Vetted vendors compete; you get one curated, transparent quote." },
  { num: "03", title: "Consolidated delivery", text: "Multiple categories, one delivery schedule, one invoice." },
  { num: "04", title: "Ongoing support", text: "Replenishment runs, audits and a Saarthi on call." },
];

export default function Home() {
  useMeta({
    title: "Saarthva | Procurement & Operations Partner in India",
    description: "Saarthva helps businesses simplify procurement, sourcing, vendor coordination and workplace operations through one accountable partner.",
    path: "/",
  });
  const { scrollY } = useScroll();
  const mandalaY = useTransform(scrollY, [0, 800], [0, 140]);
  const mandalaR = useTransform(scrollY, [0, 1000], [0, 45]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-white grain" data-testid="hero-section">
        <motion.div style={{ y: mandalaY, rotate: mandalaR }} className="absolute -right-48 top-1/2 -translate-y-1/2 pointer-events-none">
          <Mandala className="w-[760px] h-[760px] text-steel opacity-70 spin-slow" />
        </motion.div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(37,99,235,0.2),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(249,115,22,0.08),transparent_50%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-16 lg:pt-48 lg:pb-24">
          <FadeIn delay={0.1}>
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-saffron mb-7" data-testid="hero-brand-line">
              SUPPLY. SOURCE. RUN.
            </p>
          </FadeIn>
          <h1 aria-label="Your Business Saarthi." className="font-display font-extrabold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-headline">
            <MaskLine delay={0.2}>Your Business</MaskLine>
            <MaskLine delay={0.38}>
              <span className="text-saffron">Saarthi</span>
              <span className="text-steel">.</span>
            </MaskLine>
          </h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="origin-left h-1 w-44 bg-gradient-to-r from-saffron to-steel rounded-full mt-7"
          />
          <FadeIn delay={0.6}>
            <p className="mt-7 text-lg sm:text-xl text-white font-medium" data-testid="hero-subheadline">
              One Partner for Procurement & Business Operations.
            </p>
            <p className="mt-3 max-w-xl text-slate-300 leading-relaxed" data-testid="hero-supporting-copy">
              From everyday supplies and specialised sourcing to vendor management and day-to-day operations,
              Saarthva helps businesses reduce complexity and keep things running. Saarthva is a B2B procurement
              and business operations partner helping companies manage sourcing, workplace supplies, vendors and
              multi-location operational requirements across India.
            </p>
          </FadeIn>
          <FadeIn delay={0.8}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/request-quote"
                data-testid="hero-request-quote-button"
                className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-8 py-4 transition-all hover:gap-3"
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-explore-solutions-button"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white font-semibold px-8 py-4 transition-colors"
              >
                Explore Our Solutions
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={1.0}>
            <div className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 border-t border-white/10 pt-8" data-testid="hero-stats">
              {CAPABILITIES.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <p className="font-display text-base lg:text-lg font-bold text-white leading-snug">{s.title}</p>
                  <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-white border-y border-line py-5 overflow-hidden" data-testid="category-marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center shrink-0">
              {SERVICES.map((s) => (
                <span key={`${dup}-${s.id}`} className="flex items-center">
                  <span className="font-display text-sm sm:text-base font-semibold text-slate-700 tracking-wide px-6 whitespace-nowrap">
                    {s.title}
                  </span>
                  <span className="text-saffron text-xs">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="bg-white" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHO WE SERVE</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight max-w-3xl">
              Built for businesses with recurring operational complexity.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SEGMENTS.map((seg, i) => {
              const Icon = seg.icon;
              return (
                <Reveal key={seg.label} delay={(i % 3) * 0.06}>
                  <div
                    data-testid={`segment-${seg.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex items-center gap-4 rounded-xl border border-line bg-white p-5 transition-all duration-300 hover:border-saffron/50 hover:bg-cream/50"
                  >
                    <Icon className="w-5 h-5 text-steel shrink-0 transition-colors group-hover:text-saffron" strokeWidth={1.8} />
                    <p className="text-sm font-semibold text-navy">{seg.label}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="bg-white" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">THE SAARTHVA WAY</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy max-w-2xl leading-tight">
              Guided by a Saarthi's promise.
            </h2>
            <p className="mt-4 max-w-xl text-slate-600 leading-relaxed">
              In every Indian journey, the Saarthi is the one who knows the road. We bring that same
              quiet reliability to your business operations.
            </p>
          </Reveal>
          <div className="mt-14">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.08}>
                <div className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline border-t border-line py-10 last:border-b" data-testid={`manifesto-chapter-${c.num}`}>
                  <p className="md:col-span-2 font-mono text-saffron text-sm tracking-widest">{c.num}</p>
                  <h3 className="md:col-span-4 font-display text-3xl lg:text-4xl font-bold text-navy transition-transform duration-300 group-hover:translate-x-2">
                    {c.title}
                  </h3>
                  <p className="md:col-span-6 text-slate-600 leading-relaxed max-w-lg">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="bg-mist" data-testid="problems-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">THE PROBLEMS WE SOLVE</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight max-w-3xl">
              Business operations should not be a full-time exercise in chasing vendors.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROBLEMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={(i % 3) * 0.07}>
                  <div className="h-full bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200 hover:border-saffron/40" data-testid={`problem-card-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SAARTHVA ONEDESK */}
      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="onedesk-section">
        <Mandala className="absolute -right-40 -top-48 w-[520px] h-[520px] text-steel opacity-40 spin-slow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">SAARTHVA ONEDESK</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Stop chasing vendors. <span className="text-saffron">Send it to Saarthva.</span>
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
              One point of contact for sourcing, quotations, vendor coordination, delivery tracking,
              issue closure and monthly reporting.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {["One SPOC", "One Tracker", "One Monthly MIS", "One Owner till Closure"].map((t, i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur transition-all duration-300 hover:bg-white/10 hover:border-saffron/40" data-testid={`onedesk-card-${i}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-display text-xl font-bold">{t}</h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/request-quote"
                data-testid="onedesk-cta"
                className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-8 py-4 transition-colors"
              >
                Start with one requirement <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/onedesk"
                data-testid="onedesk-explore-link"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white font-semibold px-8 py-4 transition-colors"
              >
                Explore OneDesk
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPEX / CAPEX / MRO */}
      <section className="bg-white" data-testid="spend-classes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHAT WE PROCURE</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight max-w-3xl">
              Across everyday spend, strategic purchases and maintenance requirements.
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-5">
            {SPEND_CLASSES.map((sc, i) => (
              <Reveal key={sc.code} delay={i * 0.08}>
                <div className="group h-full rounded-2xl bg-navy text-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col" data-testid={`spend-class-${sc.code.toLowerCase()}`}>
                  <p className="font-display text-4xl font-extrabold text-saffron tracking-tight">{sc.code}</p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-slate-400">{sc.name.toUpperCase()}</p>
                  <p className="mt-4 text-sm text-slate-300">{sc.desc}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {sc.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-slate-200">
                        <span className="w-1.5 h-1.5 rounded-sm bg-saffron shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/products"
              data-testid="spend-classes-cta"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-saffron text-white font-semibold px-7 py-3.5 transition-colors"
            >
              Explore Procurement Categories <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CROSSOVER */}
      <Crossover />

      {/* NEW SITE SETUP */}
      <section id="new-site-setup" className="bg-navy text-white grain relative overflow-hidden scroll-mt-28" data-testid="new-site-section">
        <Mandala className="absolute -left-40 -top-48 w-[520px] h-[520px] text-steel opacity-40 spin-slow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">NEW SITE / OFFICE SETUP</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Opening a new office or location?
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">
              Give Saarthva the checklist. We coordinate sourcing and operational readiness across furniture,
              IT, pantry, housekeeping, electrical, safety, signage, vendors and recurring supplies.
            </p>
            <Link
              to="/request-quote?type=New-site setup"
              data-testid="home-new-site-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-7 py-3.5 transition-colors"
            >
              Discuss a New Site Setup <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-8 backdrop-blur" data-testid="setup-checklist">
              <p className="font-mono text-[10px] tracking-[0.3em] text-saffron mb-5">THE SETUP CHECKLIST</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "Furniture & workstations", "IT & networking", "Pantry & beverages", "Housekeeping readiness",
                  "Electrical & MRO", "Safety & compliance", "Signage & branding", "Recurring supplies",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-200">
                    <Check className="w-4 h-4 text-saffron shrink-0" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* START SMALL */}
      <section className="bg-white" data-testid="start-small-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">HOW TO BEGIN</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight max-w-2xl">
              Start small. Scale when we prove value.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { k: "01", title: "One Requirement", text: "Need an urgent or unusual item? Let Saarthva source it." },
              { k: "02", title: "One Category", text: "Consolidate one recurring category such as housekeeping, pantry or stationery." },
              { k: "03", title: "One Location", text: "Let Saarthva manage procurement or operations for one site before expanding." },
            ].map((p, i) => (
              <Reveal key={p.k} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-line bg-white p-8 border-t-2 border-t-saffron transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200" data-testid={`pathway-${p.k}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{p.k}</p>
                  <h3 className="mt-3 font-display text-xl font-bold text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <p className="text-slate-500 text-sm italic">No large transformation project required.</p>
              <Link
                to="/request-quote"
                data-testid="start-small-cta"
                className="inline-flex items-center gap-2 rounded-full bg-navy hover:bg-saffron text-white font-semibold px-7 py-3.5 transition-colors"
              >
                Start With One Requirement <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="bg-white border-t border-line" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">TIER-1 CITY NETWORK</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight">
              Built to support businesses across India's major commercial hubs.
            </h2>
            <p className="mt-4 max-w-lg text-slate-600 leading-relaxed">
              Saarthva works through a growing sourcing and operating network across major Tier-1 business
              cities, enabling local vendor coordination with central accountability.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="cities-pills">
              {CITIES.map((c) => (
                <span
                  key={c.name}
                  data-testid={`city-pill-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-[13px] font-semibold text-navy transition-colors hover:border-saffron/50 hover:bg-cream"
                >
                  <MapPin className="w-3.5 h-3.5 text-saffron" strokeWidth={2} />
                  {c.name}
                </span>
              ))}
              <span className="inline-flex items-center rounded-full border border-dashed border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-500">
                Other locations available based on requirement
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl bg-navy p-6 sm:p-10 grain" data-testid="india-map-card">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.15),transparent_60%)]" />
              <IndiaMap className="relative z-10 w-full h-auto max-h-[480px] mx-auto" />
              <div className="relative z-10 flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <p className="font-mono text-[10px] tracking-[0.3em] text-saffron">TIER-1 CITY NETWORK</p>
                <p className="text-xs text-slate-400">Local sourcing partners · central accountability</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ENGAGEMENT MODELS */}
      <section className="bg-mist" data-testid="engagement-models-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">ENGAGEMENT MODELS</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy leading-tight max-w-2xl">
              Choose how Saarthva works with you.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ENGAGEMENT_MODELS.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.title} delay={i * 0.07}>
                  <div className="h-full bg-white rounded-2xl border border-line p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200 hover:border-saffron/40" data-testid={`engagement-model-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy leading-snug">{m.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-xs text-slate-500 font-mono tracking-wide">COMMERCIALS ARE STRUCTURED PER ENGAGEMENT — TALK TO US.</p>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="process-section">
        <Mandala className="absolute -left-40 -bottom-48 w-[520px] h-[520px] text-steel opacity-40 spin-slow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <Reveal className="mb-14">
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">HOW IT WORKS</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-2xl">
              From requirement to running workplace.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.1}>
                <div className="border-t border-white/15 pt-6" data-testid={`process-step-${step.num}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{step.num}</p>
                  <h3 className="mt-3 font-display text-xl font-bold">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <a
              href={waLink("Hi Saarthva, here's my requirement:")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="process-whatsapp-link"
              className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-saffron hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Start with a WhatsApp message — it is that simple
            </a>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
